import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import CustomModal from "../basics/interface/CustomModal"
import Loader from "../basics/Loader"
import {
	fetchErrorReports,
	fetchErrorReportDetail,
	marquerErreursTraitees,
} from "../../store/actions/errorReportActions"

const FILTRES = [
	{ key: "actifs", label: "Actives" },
	{ key: "traites", label: "Traitées" },
	{ key: "tous", label: "Toutes" },
]

const PAGE_SIZE = 50

const styles = {
	content: { width: "100%", maxWidth: 1000, margin: "0 auto" },
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		marginBottom: 20,
	},
	search: { ...gs.input, maxWidth: 260 },
	badgeType: {
		fontSize: 11,
		fontWeight: 600,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.dangerLight,
		color: COLORS.danger,
		whiteSpace: "nowrap",
	},
	badgeTraite: {
		fontSize: 11,
		fontWeight: 600,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.bg,
		color: COLORS.textLight,
		whiteSpace: "nowrap",
	},
	count: {
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		marginLeft: 6,
	},
	message: {
		maxWidth: 520,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	traceback: {
		fontFamily: "monospace",
		fontSize: 12,
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		backgroundColor: COLORS.bg,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: 12,
		maxHeight: "50vh",
		overflowY: "auto",
	},
	empty: {
		padding: "40px 0",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 14,
	},
	loadMoreRow: {
		display: "flex",
		justifyContent: "center",
		marginTop: 16,
	},
}

const Row = ({ children, index, onOpen }) => {
	const [hover, setHover] = useState(false)
	return (
		<tr
			onClick={onOpen}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: hover
					? tableStyles.rowHover
					: tableStyles.rowBg(index),
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			{children}
		</tr>
	)
}

// Journal des erreurs serveur (/erreurs, boss) : les ErrorReport logués par le
// handler global — le 500 les référence (« rapport #N »). Les doublons
// (même type + message) sont regroupés à l'affichage avec un compteur ×N ;
// marquer le groupe traité traite tous ses rapports.
const ErreursPage = () => {
	const dispatch = useDispatch()
	const { reports, fetchReports } = useSelector((s) => s.errorReportsInfo)
	const [statut, setStatut] = useState("actifs")
	const [q, setQ] = useState("")
	const [hasMore, setHasMore] = useState(false)
	const [loadingMore, setLoadingMore] = useState(false)
	// Groupe ouvert dans le modal ({ group, traceback|null }).
	const [ouvert, setOuvert] = useState(null)
	const [confirm, setConfirm] = useState(false)
	const [saving, setSaving] = useState(false)
	// Confirmation « Tout marquer traité » (switch sur le bouton).
	const [confirmTous, setConfirmTous] = useState(false)

	const fetchPremierePage = () =>
		dispatch(
			fetchErrorReports(statut, { q, limit: PAGE_SIZE, offset: 0 }),
		).then((json) => setHasMore((json || []).length === PAGE_SIZE))

	// Changement d'onglet (et montage) : fetch immédiat.
	useEffect(() => {
		fetchPremierePage()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, statut])

	// Frappe dans la recherche : débouncée (300 ms) pour ne pas mitrailler le
	// serveur. Skip au montage (le fetch initial est porté par l'effet statut).
	const qMonte = useRef(false)
	useEffect(() => {
		if (!qMonte.current) {
			qMonte.current = true
			return
		}
		const t = setTimeout(fetchPremierePage, 300)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [q])

	const refetch = () => {
		const reloadLimit = Math.max(PAGE_SIZE, reports.length)
		return dispatch(
			fetchErrorReports(statut, { q, limit: reloadLimit, offset: 0 }),
		).then((json) => setHasMore((json || []).length === reloadLimit))
	}

	const chargerPlus = () => {
		setLoadingMore(true)
		dispatch(
			fetchErrorReports(statut, {
				q,
				limit: PAGE_SIZE,
				offset: reports.length,
				append: true,
			}),
		)
			.then((json) => setHasMore((json || []).length === PAGE_SIZE))
			.finally(() => setLoadingMore(false))
	}

	// Regroupement des doublons sur les rapports chargés : le représentant est
	// le plus récent (liste triée par -id côté serveur).
	const groupes = []
	const parCle = {}
	reports.forEach((r) => {
		const cle = `${r.type}|${r.message}|${r.traite}`
		if (parCle[cle]) {
			parCle[cle].count += 1
			parCle[cle].ids.push(r.id)
		} else {
			parCle[cle] = { report: r, count: 1, ids: [r.id] }
			groupes.push(parCle[cle])
		}
	})

	const ouvrir = (group) => {
		setConfirm(false)
		setOuvert({ group, traceback: null })
		dispatch(fetchErrorReportDetail(group.report.id)).then((json) => {
			if (json)
				setOuvert((prev) =>
					prev && prev.group === group
						? { ...prev, traceback: json.traceback }
						: prev,
				)
		})
	}

	const marquerTraite = () => {
		if (!confirm) {
			setConfirm(true)
			return
		}
		setSaving(true)
		dispatch(marquerErreursTraitees({ ids: ouvert.group.ids }))
			.then(() => refetch())
			.then(() => setOuvert(null))
			.finally(() => {
				setSaving(false)
				setConfirm(false)
			})
	}

	const marquerTous = () => {
		if (!confirmTous) {
			setConfirmTous(true)
			return
		}
		setConfirmTous(false)
		dispatch(marquerErreursTraitees({ tous: true })).then(() => refetch())
	}

	return (
		<div style={gs.page}>
			<div style={styles.content}>
				<div style={{ ...gs.header, alignItems: "center" }}>
					<h2 style={gs.title}>Erreurs serveur</h2>
					{statut === "actifs" && groupes.length > 0 && (
						<button
							type="button"
							style={gs.btnSecondary}
							onClick={marquerTous}
							onMouseLeave={() => setConfirmTous(false)}
						>
							{confirmTous
								? "Confirmer ?"
								: "Tout marquer traité"}
						</button>
					)}
				</div>

				<div style={styles.toolbar}>
					<div style={gs.segmented}>
						{FILTRES.map((f) => (
							<button
								key={f.key}
								type="button"
								style={gs.segment(statut === f.key)}
								onClick={() => setStatut(f.key)}
							>
								{f.label}
							</button>
						))}
					</div>
					<input
						style={styles.search}
						placeholder="Rechercher (message, type)…"
						value={q}
						onChange={(e) => setQ(e.target.value)}
					/>
				</div>

				{fetchReports.loading && !loadingMore ? (
					<Loader variant="ring" message="Chargement des erreurs…" />
				) : groupes.length === 0 ? (
					<div style={styles.empty}>
						{statut === "actifs"
							? "Aucune erreur active — tout roule."
							: "Aucune erreur."}
					</div>
				) : (
					<>
						<div style={tableStyles.container}>
							<table style={tableStyles.table}>
								<thead>
									<tr>
										<th style={tableStyles.th}>Date</th>
										<th style={tableStyles.th}>Type</th>
										<th style={tableStyles.th}>Message</th>
										<th style={tableStyles.th}>État</th>
									</tr>
								</thead>
								<tbody>
									{groupes.map((g, index) => (
										<Row
											key={`err_${g.report.id}`}
											index={index}
											onOpen={() => ouvrir(g)}
										>
											<td
												style={{
													...tableStyles.td,
													whiteSpace: "nowrap",
												}}
											>
												{g.report.datetime}
											</td>
											<td style={tableStyles.td}>
												<span style={styles.badgeType}>
													{g.report.type}
												</span>
												{g.count > 1 && (
													<span style={styles.count}>
														×{g.count}
													</span>
												)}
											</td>
											<td style={tableStyles.td}>
												<div style={styles.message}>
													{g.report.message}
												</div>
											</td>
											<td style={tableStyles.td}>
												{g.report.traite ? (
													<span
														style={
															styles.badgeTraite
														}
													>
														traité
													</span>
												) : (
													""
												)}
											</td>
										</Row>
									))}
								</tbody>
							</table>
						</div>
						{hasMore && (
							<div style={styles.loadMoreRow}>
								<button
									type="button"
									style={gs.btnSecondary}
									onClick={chargerPlus}
									disabled={loadingMore}
								>
									{loadingMore
										? "Chargement…"
										: "Charger plus"}
								</button>
							</div>
						)}
					</>
				)}

				{ouvert && (
					<CustomModal
						title={`Rapport #${ouvert.group.report.id} — ${ouvert.group.report.type}`}
						subtitle={`${ouvert.group.report.datetime}${
							ouvert.group.count > 1
								? ` · ×${ouvert.group.count} occurrences`
								: ""
						}`}
						maxWidth={860}
						onClose={() => (saving ? null : setOuvert(null))}
						buttons={[
							{
								title: "Fermer",
								styleName: "secondary",
								onClick: () => setOuvert(null),
								disabled: saving,
							},
							...(!ouvert.group.report.traite
								? [
										{
											title: saving
												? "..."
												: confirm
													? "Confirmer ?"
													: "Marquer comme traité",
											styleName: "primary",
											onClick: marquerTraite,
											disabled: saving,
										},
									]
								: []),
						]}
					>
						<div
							style={{
								fontSize: 13,
								color: COLORS.textMuted,
								marginBottom: 10,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
							}}
						>
							{ouvert.group.report.message}
						</div>
						{ouvert.traceback === null ? (
							<Loader variant="ring" size={22} />
						) : (
							<pre style={styles.traceback}>
								{ouvert.traceback || "(pas de traceback)"}
							</pre>
						)}
					</CustomModal>
				)}
			</div>
		</div>
	)
}

export default ErreursPage
